import { useEffect } from 'react';
import moment from 'moment';
import { Grid, Button, TextInput, Modal, Select, Checkbox, Typography, CronInput } from '@ts/storybook';
import act from '@flux/actions/actView';
import { WrapperEntitySelector, TimeZoneSelect, DateTimeRangeInput } from '@components/Inputs';
import { NewPaperWithPadding } from '@components/Misc';
import { cloneDeep, isEmpty } from 'lodash';
import { KPI_EXECUTE_URL } from './constants';
import { useSetState } from 'react-use';
import { IForm, TMode } from '@ts/ui-models';
import { CCreateKPI, CKPIOperation, TNestedKeys } from '@ts/ui-models';
import { generateSearchEntitySelector } from '@modules/entitySelector/entitySelector.utils';
import { CronParamsDto, StatisticTypeDto } from '@ts/ui-models';
import cronstrue from 'cronstrue/i18n';

import { useTranslation } from 'react-i18next';

interface IKpiDefinitionFormProps extends IForm {
  values: CCreateKPI;
}

const KPIsForm = ({ values, mode, onCallbackChange, requiredFields }: IKpiDefinitionFormProps) => {
  const resultOptions = values.resultOptions;
  const { i18n } = useTranslation();

  const [state, setState] = useSetState<{
    showModal: boolean;
    availableStatistics: any[];
    statisticsOnly: boolean;
    period: 'hour' | 'day' | 'week';
  }>({
    showModal: false,
    availableStatistics: [],
    statisticsOnly: resultOptions?.find(id => id == 'statistics_only') != null,
    period: 'hour',
  });

  const isViewMode = mode == 'view';

  const MINUTES = [
    {
      label: '15',
      value: 15,
    },
    {
      label: '30',
      value: 30,
    },
    {
      label: '45',
      value: 45,
    },
    {
      label: '60',
      value: 59,
    },
  ];

  const WEEK = [
    {
      label: 'Monday',
      value: 1,
    },
    {
      label: 'Tuesday',
      value: 2,
    },
    {
      label: 'Wednesday',
      value: 3,
    },
    {
      label: 'Thursday',
      value: 4,
    },
    {
      label: 'Friday',
      value: 5,
    },
    {
      label: 'Saturday',
      value: 6,
    },
    {
      label: 'Sunday',
      value: 0,
    },
  ];

  const cronExpression: CronParamsDto = {
    minute: null,
    hour: null,
    month: null,
    dayOfMonth: null,
    dayOfWeek: null,
  };

  //#region Handlers

  const onChangeEntitySelector = (entity, entityName, fkField) => {
    const value = entity ? entity.getID() : null;
    onCallbackChange(entityName)(entity);
    onCallbackChange(fkField)(value);
  };

  const onChangePeriod = (value: 'hour' | 'day' | 'week') => {
    if (value !== state.period) {
      setState({ period: value });
      handleDataPeriodType(values.scheduleConfig?.dataPeriod?.type === 'fixed');
    }
  };

  const handleDataPeriodType = checked => {
    let updated = cloneDeep(values.scheduleConfig);
    if (checked) {
      updated.dataPeriod = { type: 'fixed', params: { start: '', end: '' } };
    } else {
      updated.dataPeriod = {
        type: 'relative',
        params: {
          start: { ...cronExpression },
          end: { ...cronExpression },
        },
      };
    }
    onCallbackChange('scheduleConfig')(updated);
  };

  const onChangeDataPeriod = (key: 'start' | 'end', key2: 'minute' | 'hour' | 'dayOfMonth' | 'dayOfWeek') => (value: any) => {
    let updated = cloneDeep(values.scheduleConfig.dataPeriod);
    updated.params[key][key2] = value;
    onCallbackChange(`scheduleConfig.dataPeriod`)(updated);
  };

  const _handleModalClear = () => {
    setState({ showModal: false });
  };

  const _handleModalOk = () => {
    setState({ showModal: false });
    const name = values.execName || values.name;
    const data = {
      kpiDefinitionId: values.getID(),
      name,
    };
    act.push({ url: KPI_EXECUTE_URL(data.kpiDefinitionId), data });
  };

  const onChangeCheckStatistic = statId => checked => {
    if (!values.statisticsToCalculate) return;
    // Clonación del array
    let statisticsToCalculate = [...values.statisticsToCalculate];

    if (checked) {
      statisticsToCalculate.push(statId);
    } else {
      let idx = statisticsToCalculate.indexOf(statId);
      if (idx !== -1) statisticsToCalculate.splice(idx, 1);
    }

    onCallbackChange('statisticsToCalculate')(statisticsToCalculate);
  };

  const onChangeCheckStatisticsOnly = (checked: boolean) => {
    setState({ statisticsOnly: checked });
    if (checked) onCallbackChange('resultOptions')(['statistics_only']);
    else onCallbackChange('resultOptions')([]);
  };

  const _handleCheckResultOption = opId => checked => {
    if (!values.resultOptions) return;
    let resultOptions = [...values.resultOptions];
    if (checked) {
      resultOptions?.push(opId);
    } else {
      let idx = resultOptions?.indexOf(opId);
      if (idx) resultOptions?.splice(idx, 1);
    }
    onCallbackChange('resultOptions')(resultOptions);
  };

  /*   const _handleCheckScheduledRecurrenceEnable = checked => {
    let updatedSchedConfig = cloneDeep(values.scheduleConfig);
    updatedSchedConfig.enabled = checked;
    onChangeValue('scheduleConfig')(updatedSchedConfig);
  }; */

  const onChangeKPIOperation = (kpiOperation: CKPIOperation | null) => {
    if (kpiOperation != null) {
      const operationTypeLid = kpiOperation.getID();
      onCallbackChange('kpiOperation')(kpiOperation);
      onCallbackChange('kpiOperationId')(kpiOperation.getID());
      let resultOptions = getDefaultOperationTypeResultOptions(operationTypeLid);
      onCallbackChange('resultOptions')(resultOptions);
    } else {
      onCallbackChange('kpiOperation')(null);
      onCallbackChange('kpiOperationId')(null);
    }

    //setState({ availableStatistics: availableStatistics });
  };

  const onClickShowModal = () => {
    let name = values.name;
    values.execName = `KPI ${name}: ${moment().format()}`;
    setState({ showModal: true });
  };
  //#endregion

  //#region Functions
  const initAvailableStatistics = () => {
    let availableStatistics: StatisticTypeDto[] = [];
    if (values.kpiOperation) {
      let xOpStats = values.kpiOperation.kpiOperationType?.statisticTypes;
      if (xOpStats) {
        availableStatistics = xOpStats;
      }
    }
    setState({ availableStatistics: availableStatistics });
  };

  const getDefaultOperationTypeResultOptions = operationTypeLid => {
    let resultOptions: any[] = [];
    switch (operationTypeLid) {
      case 'geometries_presence':
        resultOptions = ['nonempty_geometries', 'empty_geometries', 'item_number_inside_geometry', 'list_items_inside_geometry'];
        break;
      default:
    }
    return resultOptions;
  };

  /*   const getOperationTypeResultOptions = operationTypeLid => {
    let specificOptions: any[] = [];
    switch (operationTypeLid) {
      case 'geometries_presence':
        specificOptions = [
          // result items
          {
            logicalId: 'nonempty_geometries',
            caption: 'List geometries with items inside',
          },
          { logicalId: 'empty_geometries', caption: 'List empty geometries' },
          // Representation of result item
          {
            logicalId: 'item_number_inside_geometry',
            caption: 'Include number of items inside each geometry',
          },
          {
            logicalId: 'list_items_inside_geometry',
            caption: 'Include list of items inside each geometry',
          },
          {
            logicalId: 'include_ids',
            caption: 'Include IDs of items and geometries',
          },
          {
            logicalId: 'include_datapoint',
            caption: 'Include position of items',
          },
        ];
        break;
      default:
    }
    return specificOptions;
  }; */

  //#endregion

  //#region Renders
  const renderDataPeriod = () => {
    const dataPeriod = values.scheduleConfig.dataPeriod;
    if (values.scheduleConfig?.dataPeriod?.type == 'fixed') {
      return (
        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <Typography variant="body1" component="h2">
            Dates
          </Typography>
          <DateTimeRangeInput
            timezone={values.scheduleConfig.timezone}
            value={[dataPeriod.params.start as string, dataPeriod.params.end as string]}
            onChange={([start, end]) => {
              onCallbackChange('scheduleConfig.dataPeriod.type')('fixed');
              if (start) onCallbackChange('scheduleConfig.dataPeriod.params.start')(start);
              onCallbackChange('scheduleConfig.dataPeriod.params.end')(end);
            }}
            isReadOnly={isViewMode}
          />
        </Grid>
      );
    }
  };

  const renderSubCustomConfig = () => {
    const dataPeriodParams = values.scheduleConfig.dataPeriod.params;
    const start: CronParamsDto = dataPeriodParams.start as CronParamsDto;
    const end: CronParamsDto = dataPeriodParams.end as CronParamsDto;
    if (values.scheduleConfig.dataPeriod.type == 'relative') {
      switch (state.period) {
        case 'hour':
          return (
            <Grid direction={'row'} container alignItems={'flex-end'} spacing={2} size={12}>
              <Typography text="Every hour from minute" />
              <Grid size={2}>
                <Select
                  isReadOnly={isViewMode}
                  value={start?.minute as number}
                  onChange={onChangeDataPeriod('start', 'minute')}
                  options={MINUTES}
                />
              </Grid>
              <Typography text="to minute" />
              <Grid size={2}>
                <Select
                  isReadOnly={isViewMode}
                  value={end?.minute as number}
                  onChange={onChangeDataPeriod('end', 'minute')}
                  options={MINUTES}
                />
              </Grid>
            </Grid>
          );
        case 'day':
          return (
            <Grid container direction={'row'} alignItems={'flex-end'} spacing={2} size={12}>
              <Typography text="Every day from hour" />
              <Grid size={2}>
                <TextInput
                  key={'startHour'}
                  isReadOnly={isViewMode}
                  label={'Start'}
                  value={start?.hour}
                  onChangeValue={onChangeDataPeriod('start', 'hour')}
                  type={'number'}
                  min={0}
                  max={23}
                />
              </Grid>
              <Typography text="to hour" />
              <Grid size={2}>
                <TextInput
                  key={'endHour'}
                  isReadOnly={isViewMode}
                  label={'End'}
                  value={end?.hour}
                  onChangeValue={onChangeDataPeriod('end', 'hour')}
                  type={'number'}
                  min={1}
                  max={24}
                />
              </Grid>
            </Grid>
          );
        case 'week':
          return (
            <Grid container direction={'row'} alignItems={'flex-end'} spacing={2} size={12}>
              <Typography text="Every week from day" />
              <Grid size={2}>
                <Select
                  isReadOnly={isViewMode}
                  value={start?.dayOfWeek as number}
                  onChange={onChangeDataPeriod('start', 'dayOfWeek')}
                  options={WEEK}
                />
              </Grid>
              <Typography text="at" />
              <Grid size={2}>
                <TextInput
                  key={'startHour'}
                  isReadOnly={isViewMode}
                  label={'Hour'}
                  value={start?.hour}
                  onChangeValue={onChangeDataPeriod('start', 'hour')}
                  type={'number'}
                  min={0}
                  max={23}
                />
              </Grid>
              <Typography text="to day" />
              <Grid size={2}>
                <Select
                  isReadOnly={isViewMode}
                  value={end?.dayOfWeek}
                  onChange={onChangeDataPeriod('end', 'dayOfWeek')}
                  options={WEEK}
                />
              </Grid>
              <Typography text="at" />
              <Grid size={2}>
                <TextInput
                  key={'endHour'}
                  isReadOnly={isViewMode}
                  label={'Hour'}
                  value={end?.hour}
                  onChangeValue={onChangeDataPeriod('end', 'hour')}
                  type={'number'}
                  min={1}
                  max={24}
                />
              </Grid>
            </Grid>
          );
        default:
          return;
      }
    } else return;
  };

  const renderDataPeriodCheckbox = () => {
    return (
      <Grid container direction="column" size={12}>
        <Typography variant="subtitle1" gutterBottom text="When calculating, use the data in the following period of time" />
        <Checkbox
          isReadOnly={isViewMode}
          label="Fixed data period"
          checked={values.scheduleConfig?.dataPeriod?.type === 'fixed'}
          onChangeValue={handleDataPeriodType}
        />
        {renderDataPeriod()}
        {renderSubCustomConfig()}
      </Grid>
    );
  };

  const renderScheduled = () => {
    const CompCronInput = (
      <CronInput
        value={values.scheduleConfig.schedule}
        minValue={'0 * * * *'}
        onChange={onCallbackChange('scheduleConfig.schedule')}
        onChangePeriod={onChangePeriod}
      />
    );
    if (values.scheduleConfig?.enabled)
      return (
        <>
          <Grid size={4}>
            <TimeZoneSelect
              title={['Select Time Zone']}
              isReadOnly={isViewMode}
              value={values.scheduleConfig.timezone}
              onChange={onCallbackChange('scheduleConfig.timezone')}
            />
          </Grid>
          <Grid size={12}>
            {isViewMode ? (
              <>
                <Typography variant="h6">
                  {cronstrue.toString(values.scheduleConfig.schedule, { locale: i18n.language })}
                </Typography>
                <div className="hidden">
                  {/* We need this because the backend doesn't send the period and we need to calculate the period in CronInput */}
                  {CompCronInput}
                </div>
              </>
            ) : (
              CompCronInput
            )}
          </Grid>

          {renderDataPeriodCheckbox()}
        </>
      );
  };

  const renderModal = () => {
    return (
      <Modal
        withHeader
        title="Name of execution"
        labelOk="Ok"
        labelCancel="Close"
        onClose={_handleModalClear}
        open={state.showModal}
        onSubmit={_handleModalOk}
        fullWidth
        maxWidth="md"
      >
        <Grid container spacing={4} p={2}>
          <TextInput isReadOnly={false} label={'Name'} value={values.execName} onChangeValue={onCallbackChange('execName')} />
        </Grid>
      </Modal>
    );
  };

  const renderMain = () => {
    return (
      <Grid container spacing={2}>
        <Grid container justifyContent={'space-between'} size={12}>
          <Typography variant="h6" text="Main Information" />
          <Button label="Calculate Now" disabled={mode != 'view'} onClick={onClickShowModal} />
        </Grid>
        <TextInput
          isReadOnly={isViewMode}
          label="Name"
          value={values.name}
          onChangeValue={onCallbackChange('name')}
          required={requiredFields.includes('name')}
          infoText="Blank spaces at the beggining and the end will be trimmed on save"
        />
        <WrapperEntitySelector
          label={'KPI Operation'}
          searchComponent={generateSearchEntitySelector('kpiOperation')}
          value={values.kpiOperation as any}
          onChangeEntity={v => onChangeKPIOperation(v as CKPIOperation | null)}
          isReadOnly={isViewMode}
          required={requiredFields.includes('kpiOperation')}
        />
        <WrapperEntitySelector
          label={'Ruleset'}
          searchComponent={generateSearchEntitySelector('ruleset')}
          value={values.ruleset}
          onChangeEntity={entity => onChangeEntitySelector(entity, 'ruleset', 'rulesetId')}
          isReadOnly={isViewMode}
          required={requiredFields.includes('ruleset')}
        />
      </Grid>
    );
  };

  const renderValuesToCalculate = () => {
    let statisticOptions: any[] = [];
    let resultOptions = values.resultOptions;
    let resultOptionsCheckboxes: any[] = [];

    if (state.availableStatistics && state.availableStatistics.length) {
      let stats = state.availableStatistics.map(availableStat => {
        let statisticsToCalculate = values.statisticsToCalculate;
        let found = statisticsToCalculate?.find(s => s == availableStat.logicalId);
        let checked = found != null;
        return (
          <Checkbox
            isReadOnly={isViewMode}
            key={availableStat.logicalId}
            label={availableStat.name}
            checked={checked}
            onChangeValue={onChangeCheckStatistic(availableStat.logicalId)}
          />
        );
      });
      statisticOptions = statisticOptions.concat(stats);
      // Result options
      resultOptionsCheckboxes = [
        <Checkbox
          key="statistics_only"
          isReadOnly={isViewMode}
          label="Include statistics only"
          checked={resultOptions?.find(o => o == 'statistics_only') != null}
          onChangeValue={onChangeCheckStatisticsOnly}
        />,
      ];
    }

    //let availableResultOptions = getOperationTypeResultOptions(operationTypeLid);
    let availableResultOptions: any[] = [];
    if (!isEmpty(availableResultOptions)) {
      //
      availableResultOptions.forEach(availableOption => {
        // check if the available option is in the resultOptions array
        let found = resultOptions?.find(o => o == availableOption.logicalId);
        let checked = found != null;
        resultOptionsCheckboxes.push(
          <Checkbox
            isReadOnly={isViewMode || state.statisticsOnly}
            key={availableOption.logicalId}
            label={availableOption.caption}
            checked={checked}
            onChangeValue={_handleCheckResultOption(availableOption.logicalId)}
          />
        );
      });
    }

    return (
      <Grid container size={12}>
        <Grid size={6}>
          <Typography variant="subtitle1" text={[<b>Statistics</b>]} />
          {statisticOptions}
        </Grid>
        <Grid size={6}>
          <Typography variant="subtitle1" text={[<b>Results options</b>]} />
          {resultOptionsCheckboxes}
        </Grid>
      </Grid>
    );
  };

  //#endregion

  useEffect(() => {
    initAvailableStatistics();
  }, [values.kpiOperation]);

  return (
    <Grid container size={12} spacing={2}>
      <NewPaperWithPadding>{renderMain()}</NewPaperWithPadding>
      {state.availableStatistics.length > 0 && (
        <NewPaperWithPadding>
          <Grid container spacing={2}>
            <Grid size={12}>
              <Typography variant="h6" text="Values to calculate and include in the result" />
            </Grid>
            {renderValuesToCalculate()}
          </Grid>
        </NewPaperWithPadding>
      )}
      <NewPaperWithPadding>
        <Grid container spacing={2}>
          <Grid size={12}>
            <Typography variant="h6" text="This KPI shall be calculated" />
          </Grid>
          <Checkbox
            isReadOnly={isViewMode}
            label="Enable scheduled recurrent calculations"
            checked={values.scheduleConfig?.enabled}
            onChangeValue={onCallbackChange('scheduleConfig.enabled')}
          />
          <Grid container spacing={2} size={12}>
            {renderScheduled()}
          </Grid>
        </Grid>
      </NewPaperWithPadding>
      {renderModal()}
    </Grid>
  );
};

export default KPIsForm;
